import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, FileText, Clock, CheckCircle, Microscope, Filter } from 'lucide-react';
import { labOrderService, labOrderItemService, LabOrder } from '../../lib/services.lab';
import { patientService } from '../../lib/services';
import { useStore } from '../../lib/store';
import { can, getUserRoleFromDB } from '../../lib/rbac';
import toast from 'react-hot-toast';

interface OrderWithDetails extends LabOrder {
  itemCount?: number;
}

export default function Worklist() {
  const { user } = useStore();
  const [orders, setOrders] = useState<OrderWithDetails[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'collection' | 'processing' | 'verification' | 'finalized'>('collection');
  const [search, setSearch] = useState('');
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    loadUserRole();
    loadOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, activeTab, search]);

  const loadUserRole = async () => {
    if (!user?.id) return;
    try {
      const role = await getUserRoleFromDB(user.id);
      setUserRole(role || '');
    } catch (error) {
      console.error('Error loading user role:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const ordersData = await labOrderService.getAll();
      
      const ordersWithDetails = await Promise.all(
        ordersData.map(async (order) => {
          try {
            const items = await labOrderItemService.getByOrder(order.id);
            return {
              ...order,
              itemCount: items.length,
            };
          } catch (error) {
            return { ...order, itemCount: 0 };
          }
        })
      );

      setOrders(ordersWithDetails as OrderWithDetails[]);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load lab orders');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = orders;

    const statusMap = {
      collection: 'ordered',
      processing: 'collected',
      verification: 'in_progress',
      finalized: 'finalized'
    };

    filtered = filtered.filter(order => 
      order.status === statusMap[activeTab] || 
      (activeTab === 'verification' && order.status === 'verified')
    );

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(order =>
        order.patient?.name?.toLowerCase().includes(searchLower) ||
        order.id.toLowerCase().includes(searchLower)
      );
    }

    setFilteredOrders(filtered);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      ordered: 'bg-yellow-100 text-yellow-800',
      collected: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      verified: 'bg-green-100 text-green-800',
      finalized: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority?: string) => {
    const badges = {
      stat: 'bg-red-100 text-red-800',
      urgent: 'bg-yellow-100 text-yellow-800',
      routine: 'bg-green-100 text-green-800'
    };
    return badges[(priority || 'routine') as keyof typeof badges] || 'bg-green-100 text-green-800';
  };

  const getPaymentBadge = (paymentStatus?: string) => {
    const badges = {
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-red-100 text-red-800'
    };
    return badges[(paymentStatus || 'pending') as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentDisplay = (order: OrderWithDetails) => {
    if (!order.invoice_id || !order.invoice) {
      return { status: 'pending', text: 'PENDING' };
    }

    const total = order.invoice.total || 0;
    const paid = (order.invoice as any).paid_amount || 0;

    if (paid >= total) {
      return { status: 'paid', text: `PAID (৳${total.toFixed(2)})` };
    } else if (paid > 0) {
      return { status: 'partial', text: `PARTIAL (৳${paid.toFixed(2)}/৳${total.toFixed(2)})` };
    } else {
      return { status: 'pending', text: `PENDING (৳${total.toFixed(2)})` };
    }
  };

  const formatTimeUntil = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    
    if (diffMs <= 0) return null;
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMins}m`;
  };

  const getTabCounts = () => {
    return {
      collection: orders.filter(o => o.status === 'ordered').length,
      processing: orders.filter(o => o.status === 'collected').length,
      verification: orders.filter(o => ['in_progress', 'verified'].includes(o.status)).length,
      finalized: orders.filter(o => o.status === 'finalized').length
    };
  };

  const counts = getTabCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lab Worklist</h1>
          <p className="text-sm text-neutral-600">Manage laboratory orders and samples</p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/lab/catalog"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Microscope className="w-4 h-4 mr-2" />
            Test Catalog
          </Link>
          {(userRole === 'admin' || userRole === 'receptionist') && (
            <Link
              to="/lab/orders/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Order
            </Link>
          )}
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Collection</p>
              <p className="text-2xl font-bold text-neutral-900">{counts.collection}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Processing</p>
              <p className="text-2xl font-bold text-neutral-900">{counts.processing}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Microscope className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Verification</p>
              <p className="text-2xl font-bold text-neutral-900">{counts.verification}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Finalized</p>
              <p className="text-2xl font-bold text-neutral-900">{counts.finalized}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {[
              { key: 'collection', label: 'Collection', count: counts.collection },
              { key: 'processing', label: 'Processing', count: counts.processing },
              { key: 'verification', label: 'Verification', count: counts.verification },
              { key: 'finalized', label: 'Finalized', count: counts.finalized }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className="ml-2 px-2 py-1 text-xs rounded-full bg-gray-100">
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Search */}
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by patient name or order ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Orders Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tests</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due In</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {order.id.substring(0, 8)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {order.patient?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {order.itemCount} test(s)
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                      {order.source}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPriorityBadge(order.priority)}`}>
                        {(order.priority || 'routine').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(order.status)}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const payment = getPaymentDisplay(order);
                        return (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentBadge(payment.status)}`}>
                            {payment.text}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {order.due_at ? (
                        formatTimeUntil(order.due_at) ? (
                          <span className="text-gray-900">{formatTimeUntil(order.due_at)}</span>
                        ) : (
                          <span className="text-red-600 font-semibold">Overdue</span>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/lab/orders/${order.id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </Link>
                        {order.invoice_id ? (
                          <Link
                            to={`/billings/invoices/${order.invoice_id}/print`}
                            target="_blank"
                            className="text-green-600 hover:text-green-900"
                          >
                            Print Invoice
                          </Link>
                        ) : (
                          <Link
                            to={`/billings/invoices/new?lab_order_id=${order.id}`}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Create Invoice
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
